/**
 * Backend returns LocalDateTime strings without timezone (e.g. "2024-01-15T09:30:00").
 * JavaScript parses bare ISO strings as local time, but the server stores in UTC.
 * Appending "Z" forces UTC interpretation so toLocaleString() converts to the
 * browser's local timezone correctly.
 */
function asUtc(str) {
	if (!str) return null;
	if (typeof str !== "string") return new Date(str);
	// Already has explicit timezone info — leave it alone
	if (str.endsWith("Z") || /[+-]\d{2}:\d{2}$/.test(str)) {
		return new Date(str);
	}
	return new Date(str + "Z");
}

const DATE_TIME_OPTS = {
	year: "numeric",
	month: "short",
	day: "numeric",
	hour: "2-digit",
	minute: "2-digit",
};

const DATE_OPTS = {
	year: "numeric",
	month: "short",
	day: "numeric",
};

const DATE_TIME_SHORT_OPTS = {
	month: "short",
	day: "numeric",
	hour: "2-digit",
	minute: "2-digit",
};

/** Full date + time in user's local timezone, e.g. "Jan 15, 2024, 09:30 AM" */
export function formatDateTime(str) {
	const d = asUtc(str);
	if (!d || isNaN(d.getTime())) return "—";
	return d.toLocaleString(undefined, DATE_TIME_OPTS);
}

/** Short date + time (no year), e.g. "Jan 15, 09:30 AM" */
export function formatDateTimeShort(str) {
	const d = asUtc(str);
	if (!d || isNaN(d.getTime())) return "—";
	return d.toLocaleString(undefined, DATE_TIME_SHORT_OPTS);
}

/** Date only, e.g. "Jan 15, 2024" */
export function formatDate(str) {
	const d = asUtc(str);
	if (!d || isNaN(d.getTime())) return "—";
	return d.toLocaleDateString(undefined, DATE_OPTS);
}

/** Relative time, e.g. "5m ago", "2h ago" */
export function timeAgo(str) {
	const d = asUtc(str);
	if (!d || isNaN(d.getTime())) return "";
	const diff = Date.now() - d.getTime();
	const mins = Math.floor(diff / 60000);
	if (mins < 1) return "just now";
	if (mins < 60) return `${mins}m ago`;
	const hrs = Math.floor(mins / 60);
	if (hrs < 24) return `${hrs}h ago`;
	return `${Math.floor(hrs / 24)}d ago`;
}

/**
 * Milliseconds between two backend timestamps (or epoch-ms numbers), parsed as
 * UTC like the formatters above. Null if either is missing or unparseable.
 */
export function elapsedMs(start, end) {
	const a = asUtc(start);
	const b = asUtc(end);
	if (!a || !b || isNaN(a.getTime()) || isNaN(b.getTime())) return null;
	return b.getTime() - a.getTime();
}

/** Human duration, e.g. "850ms", "20.7s", "4m 05s", "1h 02m 03s" */
export function formatElapsed(ms) {
	if (ms == null || isNaN(ms) || ms < 0) return "—";
	if (ms < 1000) return `${Math.round(ms)}ms`;
	if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
	const totalSec = Math.floor(ms / 1000);
	const h = Math.floor(totalSec / 3600);
	const m = Math.floor((totalSec % 3600) / 60);
	const s = String(totalSec % 60).padStart(2, "0");
	return h ? `${h}h ${String(m).padStart(2, "0")}m ${s}s` : `${m}m ${s}s`;
}

/**
 * A browser timestamp (ms) as the backend's LocalDateTime: UTC without the
 * trailing "Z", which Spring's LocalDateTime deserializer doesn't expect
 * (the executor's toBackendDateTime does the same).
 */
export function toBackendDateTime(ms) {
	return new Date(ms).toISOString().replace(/Z$/, "");
}
